import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ChevronsLeft, ChevronsRight, ChevronRight, LayoutGrid, Settings2, LogOut, Star } from "lucide-react";
import { useLangDruk } from "@/components/menu/MenuSnelActie";
import { MenuZoeker } from "@/components/nav/MenuZoeker";
import { rolLabel } from "@/lib/rollen";


export function HubBadge({ n, neutraal = false, testid = "badge-count" }) {
  if (!n) return null;
  return (
    <span className={`inline-flex items-center justify-center h-[18px] min-w-[18px] px-1 rounded-full text-[10px] font-bold ${neutraal ? "bg-slate-700 text-slate-200" : "bg-red-500 text-white"}`} data-testid={testid}>
      {n > 99 ? "99+" : n}
    </span>
  );
}

function FavorietKnop({ it, collapsed, count, onSnelActie }) {
  const Icon = it.icon || Star;
  const druk = useLangDruk(() => onSnelActie?.(it));
  return (
    <NavLink to={it.to} data-testid={`hub-fav-${it.tid}`} title={collapsed ? it.label : undefined} {...(onSnelActie ? druk : {})}
      className={({ isActive }) => `group flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] transition-colors duration-150 select-none ${isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"} ${collapsed ? "justify-center" : ""}`}>
      <Icon className="h-4 w-4 shrink-0 text-amber-400/90" />
      {!collapsed && <span className="truncate flex-1">{it.label}</span>}
      {!collapsed && <HubBadge n={count} />}
    </NavLink>
  );
}

function HubKnop({ hub, count, actief, open, collapsed, onClick }) {
  const Icon = hub.icon;
  return (
    <button type="button" onClick={onClick} aria-haspopup="true" aria-expanded={open} data-testid={`hub-btn-${hub.key}`} title={collapsed ? hub.label : undefined}
      className={`relative w-full flex items-center gap-2.5 rounded-md py-2 pr-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
        collapsed ? "justify-center pl-2" : "pl-2.5"} ${open ? "bg-slate-700 text-white" : actief ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
      {actief && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-red-500" aria-hidden="true" />}
      <span className={`h-7 w-7 rounded-md ${hub.kleur} text-white flex items-center justify-center shrink-0 shadow-sm`}><Icon className="h-4 w-4" /></span>
      {!collapsed && <span className="truncate flex-1 text-[13px] font-semibold tracking-tight">{hub.label}</span>}
      {!collapsed && <HubBadge n={count} />}
      {!collapsed && <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform duration-150 ${open ? "translate-x-0.5 text-white" : ""}`} />}
      {collapsed && count > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-slate-900" data-testid={`hub-dot-${hub.key}`} />}
    </button>
  );
}

/** Donkere verticale hub-balk (HubSpot-achtig): logo, bladwijzers, 12 hubs, Alle tools, menu aanpassen en account. */
export function HubBar({ hubs, counts, actiefDomein, openHub, onHub, favorieten, collapsed, onToggleCollapsed, onMenuInstellingen, user, onLogout, onSnelActie, barRef }) {
  const nav = useNavigate();
  const hubTotaal = (h) => h.items.reduce((s, it) => s + (it.counter ? counts[it.counter] || 0 : 0), 0);
  return (
    <nav ref={barRef} role="navigation" aria-label="Hoofdmenu" data-testid="hub-bar-root" data-collapsed={collapsed}
      className={`hidden lg:flex flex-col shrink-0 h-screen sticky top-0 bg-slate-900 text-slate-100 border-r border-slate-800 transition-[width] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] ${collapsed ? "w-[68px]" : "w-60"}`}>
      <div className={`flex items-center gap-2.5 px-3 h-14 border-b border-slate-800 ${collapsed ? "justify-center" : ""}`}>
        <button type="button" onClick={() => nav("/dashboard")} className="flex items-center gap-2.5 min-w-0" data-testid="hub-logo-btn" title="Naar dashboard">
          <img src="/icon-192.png" alt="BPT" className="h-8 w-8 rounded-lg shadow-sm shrink-0" />
          {!collapsed && <span className="text-sm font-extrabold tracking-[0.12em] text-white uppercase" data-testid="app-name">BPTplus</span>}
        </button>
        {!collapsed && (
          <button type="button" onClick={onToggleCollapsed} className="ml-auto p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" aria-label="Menu inklappen" data-testid="hub-bar-collapse-toggle">
            <ChevronsLeft className="h-4 w-4" />
          </button>
        )}
      </div>
      {collapsed && (
        <button type="button" onClick={onToggleCollapsed} className="mx-auto mt-2 p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" aria-label="Menu uitklappen" data-testid="hub-bar-collapse-toggle">
          <ChevronsRight className="h-4 w-4" />
        </button>
      )}
      <MenuZoeker hubs={hubs} donker collapsed={collapsed} onExpand={onToggleCollapsed} />
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 space-y-4 [scrollbar-width:thin] [scrollbar-color:#334155_transparent]">
        {favorieten?.length > 0 && (
          <div data-testid="hub-favorites-section">
            {!collapsed && <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Bladwijzers</div>}
            <div className="space-y-0.5">
              {favorieten.slice(0, 10).map((it) => <FavorietKnop key={it.to} it={it} collapsed={collapsed} count={it.counter ? counts[it.counter] || 0 : 0} onSnelActie={onSnelActie} />)}
            </div>
          </div>
        )}
        <div>
          {!collapsed && <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Hubs</div>}
          <div className="space-y-0.5">
            {hubs.map((h) => <HubKnop key={h.key} hub={h} count={hubTotaal(h)} actief={h.key === actiefDomein} open={openHub === h.key} collapsed={collapsed} onClick={() => onHub(h.key)} />)}
          </div>
        </div>
      </div>
      <div className="border-t border-slate-800 p-2 space-y-0.5">
        <NavLink to="/alle-tools" data-testid="nav-all-tools-btn" title={collapsed ? "Alle tools" : undefined}
          className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors ${isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"} ${collapsed ? "justify-center" : ""}`}>
          <LayoutGrid className="h-4 w-4 shrink-0" />{!collapsed && <span>Alle tools</span>}
        </NavLink>
        <button type="button" onClick={onMenuInstellingen} data-testid="menu-aanpassen-btn" data-rechten="lezen" title={collapsed ? "Menu aanpassen" : undefined}
          className={`w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] text-slate-300 hover:bg-slate-800 hover:text-white transition-colors ${collapsed ? "justify-center" : ""}`}>
          <Settings2 className="h-4 w-4 shrink-0" />{!collapsed && <span>Menu aanpassen</span>}
        </button>
        <div className={`flex items-center gap-2.5 rounded-md px-2 py-2 ${collapsed ? "justify-center" : ""}`} data-testid="hub-user-block">
          <div className="relative shrink-0">
            <div className="h-8 w-8 rounded-full bg-slate-700 text-white flex items-center justify-center text-xs font-bold" title={user?.name}>{(user?.name || "?").slice(0, 1).toUpperCase()}</div>
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-slate-900" aria-hidden="true" />
          </div>
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-white truncate" data-testid="current-user-name">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{rolLabel(user?.role)}</div>
              </div>
              <button type="button" onClick={onLogout} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" aria-label="Uitloggen" title="Uitloggen" data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
